import React, { useState } from 'react';
import { DRAFT_SYNC_LABELS, MAX_DRAFT_PHOTOS } from '../domain/drafts';
import type { NewDraftPhoto, UnsyncedDraft, UnsyncedPhoto } from '../domain/drafts';
import { useDraftOutbox } from '../hooks/useDraftOutbox';
import { useObjectUrl } from '../hooks/useObjectUrl';
import { readPhotoSize } from '../utils/photos';
import { ActionError } from './PageStates';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ArrowLeft, ArrowRight, ImagePlus, Trash2 } from 'lucide-react';
import { ErrorText } from './ErrorText';

/**
 * An unsynced draft's photos (021 FR-009b): remove, move earlier or later, and
 * add, each saved on the page at once and carried by the draft's sync. A photo
 * the service refused shows "Couldn't add this photo" and can be removed
 * (FR-009). The 20-photo limit holds before sync (FR-009e).
 */
export default function UnsyncedPhotoEditor({ draft }: { draft: UnsyncedDraft }) {
  const { outbox } = useDraftOutbox();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const photos = draft.photos;
  const room = MAX_DRAFT_PHOTOS - photos.length;

  const save = (photoIds: string[], addPhotos?: NewDraftPhoto[]) => {
    try {
      outbox.save(draft.id, { photoIds, addPhotos });
      setError(null);
    } catch (e) {
      setError(e);
    }
  };

  const move = (index: number, by: -1 | 1) => {
    const order = photos.map((p) => p.id);
    [order[index], order[index + by]] = [order[index + by], order[index]];
    save(order);
  };

  const add = async (files: File[]) => {
    if (files.length === 0) return;
    const kept = files.slice(0, room);
    const read = await Promise.allSettled(kept.map(readPhotoSize));
    const added = read.flatMap((r, i) =>
      r.status === 'fulfilled' ? [{ file: kept[i], ...r.value }] : [],
    );
    const unreadable = kept.length - added.length;
    const notes = [
      files.length > room &&
        `A listing can have up to ${MAX_DRAFT_PHOTOS} photos. Added the first ${room}.`,
      unreadable > 0 &&
        `${unreadable === 1 ? "1 file isn't" : `${unreadable} files aren't`} a photo this browser can read.`,
    ].filter(Boolean);
    setNotice(notes.length > 0 ? notes.join(' ') : null);
    const current = outbox.get(draft.id);
    if (added.length > 0 && current)
      save(
        current.photos.map((p) => p.id),
        added,
      );
  };

  return (
    <section aria-labelledby="photos-heading" className="space-y-4 rounded-3xl bg-card p-6">
      <div>
        <h2 id="photos-heading" className="text-xl font-semibold text-foreground">
          Photos
        </h2>
        <p className="text-sm text-muted-foreground">
          Saved on this device; syncs when you&apos;re online.
        </p>
      </div>

      {photos.length === 0 ? (
        <p className="text-sm text-muted-foreground">No photos.</p>
      ) : (
        <ul aria-label="Photos" className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {photos.map((photo, i) => (
            <PhotoTile
              key={photo.id}
              photo={photo}
              n={i + 1}
              total={photos.length}
              onEarlier={i > 0 ? () => move(i, -1) : undefined}
              onLater={i < photos.length - 1 ? () => move(i, 1) : undefined}
              onRemove={() => save(photos.filter((p) => p.id !== photo.id).map((p) => p.id))}
            />
          ))}
        </ul>
      )}

      {error !== null && <ActionError error={error} />}
      {notice && (
        <p role="status" className="rounded-2xl bg-secondary p-3 text-sm text-foreground">
          {notice}
        </p>
      )}

      {room > 0 ? (
        <div>
          <input
            id="unsynced-add-photos"
            type="file"
            accept="image/*"
            multiple
            className="peer sr-only"
            onChange={(e) => {
              void add(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
          />
          <Label
            htmlFor="unsynced-add-photos"
            className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-full bg-secondary px-4 font-medium text-secondary-foreground peer-focus-visible:ring-2 peer-focus-visible:ring-ring"
          >
            <ImagePlus className="size-4" aria-hidden="true" />
            Add photos
          </Label>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          A listing can have up to {MAX_DRAFT_PHOTOS} photos.
        </p>
      )}
    </section>
  );
}

function PhotoTile({
  photo,
  n,
  total,
  onEarlier,
  onLater,
  onRemove,
}: {
  photo: UnsyncedPhoto;
  n: number;
  total: number;
  onEarlier?: () => void;
  onLater?: () => void;
  onRemove: () => void;
}) {
  const url = useObjectUrl(photo.file);
  return (
    <li className="space-y-2">
      <div className="aspect-square w-full overflow-hidden rounded-2xl bg-muted">
        {url && <img src={url} alt={`${n} of ${total}`} className="size-full object-cover" />}
      </div>
      {photo.failure !== null && (
        <ErrorText className="justify-center text-sm font-medium">
          {DRAFT_SYNC_LABELS.photoFailed}
        </ErrorText>
      )}
      <div className="flex justify-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Move photo ${n} earlier`}
          disabled={!onEarlier}
          onClick={onEarlier}
        >
          <ArrowLeft />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Move photo ${n} later`}
          disabled={!onLater}
          onClick={onLater}
        >
          <ArrowRight />
        </Button>
        <Button variant="ghost" size="icon" aria-label={`Remove photo ${n}`} onClick={onRemove}>
          <Trash2 />
        </Button>
      </div>
    </li>
  );
}
